import React, { useRef } from 'react';
import { MapPin, Phone, Mail, Globe, Play, Camera } from 'lucide-react';
import type {
  FooterData,
  FooterLink,
  FooterSocialLink,
} from 'types/websiteBuilder';
import { compressImageFilePng } from 'utils/imageUtils';
import logoPlaceholder from 'assets/websiteBuilderDefaults/logo-placeholder.svg';
import styles from './FooterSection.module.scss';

interface FooterSectionEditorProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

const DEFAULT_FOOTER_DATA = {
  brandTitle: 'Your Site Title',
  brandSubtitle: 'Supporting your mission',
  brandDescription:
    'We are committed to providing value and excellence in everything we do.',
  contactAddress: '123 Main St, City, State, 12345',
  contactPhone: '+1 (555) 000-0000',
  contactEmail: 'hello@yoursite.com',
  connectTagline: 'Stay connected with us on social media.',
  copyrightText: `© ${new Date().getFullYear()} Abrasoft. All rights reserved.`,
};

const DEFAULT_SOCIAL_LINKS: FooterSocialLink[] = [
  { iconUrl: '', url: 'https://www.facebook.com/', visible: true },
  { iconUrl: '', url: 'https://www.youtube.com/', visible: true },
  { iconUrl: '', url: 'https://www.instagram.com/', visible: true },
];

const SOCIAL_HOME_URLS = [
  'https://www.facebook.com/',
  'https://www.youtube.com/',
  'https://www.instagram.com/',
];

function normalizeQuickLink(link: FooterLink): FooterLink {
  const label = (link.label ?? '').trim();
  const rawUrl = (link.url ?? '').trim();
  const isSermons = label.toLowerCase() === 'sermons';

  if (isSermons) {
    const looksExternal = /^https?:\/\//i.test(rawUrl);
    return {
      ...link,
      url: looksExternal ? rawUrl : 'https://www.youtube.com/',
    };
  }

  return { ...link, url: rawUrl || '/' };
}

function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function renderDefaultSocialIcon(index: number) {
  if (index === 0) return <Globe size={16} strokeWidth={2} />;
  if (index === 1) return <Play size={16} strokeWidth={2} />;
  if (index === 2) return <Camera size={16} strokeWidth={2} />;
  return null;
}

function getData(data: Record<string, unknown>): FooterData {
  const links = Array.isArray(data.links)
    ? (data.links as Record<string, unknown>[]).map((l) => ({
        label: typeof l.label === 'string' ? l.label : '',
        url: typeof l.url === 'string' ? l.url : '/',
      }))
    : [];
  const socialLinks = Array.isArray(data.socialLinks)
    ? (data.socialLinks as Record<string, unknown>[]).map((s) => ({
        iconUrl: typeof s.iconUrl === 'string' ? s.iconUrl : '',
        url: typeof s.url === 'string' ? s.url : '/',
        visible: typeof s.visible === 'boolean' ? s.visible : true,
      }))
    : [];
  return {
    copyrightText:
      typeof data.copyrightText === 'string' && data.copyrightText.trim()
        ? data.copyrightText
        : DEFAULT_FOOTER_DATA.copyrightText,
    links:
      links.length > 0
        ? links.map(normalizeQuickLink)
        : [
            { label: 'About Us', url: '/www/jcsw-church/about-us' },
            { label: 'Ministries', url: '/www/jcsw-church/ministries' },
            { label: 'Events', url: '/www/jcsw-church/events' },
            { label: 'Sermons', url: 'https://www.youtube.com/' },
          ].map(normalizeQuickLink),
    socialLinks:
      socialLinks.length > 0
        ? socialLinks.map((s, index) => {
            const fallbackUrl = SOCIAL_HOME_URLS[index] ?? '/';
            const rawUrl = (s.url ?? '').trim();
            return {
              ...s,
              url: !rawUrl || rawUrl === '/' ? fallbackUrl : s.url,
            };
          })
        : DEFAULT_SOCIAL_LINKS,
    brandLogoUrl:
      typeof data.brandLogoUrl === 'string' ? data.brandLogoUrl : '',
    brandTitle:
      typeof data.brandTitle === 'string' && data.brandTitle.trim()
        ? data.brandTitle
        : DEFAULT_FOOTER_DATA.brandTitle,
    brandSubtitle:
      typeof data.brandSubtitle === 'string' && data.brandSubtitle.trim()
        ? data.brandSubtitle
        : DEFAULT_FOOTER_DATA.brandSubtitle,
    brandDescription:
      typeof data.brandDescription === 'string' && data.brandDescription.trim()
        ? data.brandDescription
        : DEFAULT_FOOTER_DATA.brandDescription,
    contactAddress:
      typeof data.contactAddress === 'string' && data.contactAddress.trim()
        ? data.contactAddress
        : DEFAULT_FOOTER_DATA.contactAddress,
    contactPhone:
      typeof data.contactPhone === 'string' && data.contactPhone.trim()
        ? data.contactPhone
        : DEFAULT_FOOTER_DATA.contactPhone,
    contactEmail:
      typeof data.contactEmail === 'string' && data.contactEmail.trim()
        ? data.contactEmail
        : DEFAULT_FOOTER_DATA.contactEmail,
    connectTagline:
      typeof data.connectTagline === 'string' && data.connectTagline.trim()
        ? data.connectTagline
        : DEFAULT_FOOTER_DATA.connectTagline,
  };
}

function getSocialHref(url: string | undefined, index: number): string {
  const raw = (url ?? '').trim();
  if (!raw || raw === '/') {
    return SOCIAL_HOME_URLS[index] ?? '/';
  }
  return raw;
}

export function FooterSectionEditor({
  data,
  onUpdateData,
}: FooterSectionEditorProps) {
  const d = getData(data);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const brandLogoInputRef = useRef<HTMLInputElement>(null);
  const uploadIndexRef = useRef<number>(-1);

  const updateLink = (index: number, field: 'label' | 'url', value: string) => {
    const next = [...(d.links ?? [])];
    if (!next[index]) next[index] = { label: '', url: '/' };
    next[index] = { ...next[index], [field]: value };
    onUpdateData({ ...data, links: next });
  };

  const updateSocial = (index: number, patch: Partial<FooterSocialLink>) => {
    const next = [...(d.socialLinks ?? [])];
    if (!next[index]) next[index] = { iconUrl: '', url: '/', visible: true };
    next[index] = { ...next[index], ...patch };
    onUpdateData({ ...data, socialLinks: next });
  };

  const addLink = () => {
    onUpdateData({
      ...data,
      links: [...(d.links ?? []), { label: '', url: '/' }],
    });
  };

  const removeLink = (index: number) => {
    const next = (d.links ?? []).filter((_, i) => i !== index);
    onUpdateData({ ...data, links: next });
  };

  const removeSocial = (index: number) => {
    const next = (d.socialLinks ?? []).filter((_, i) => i !== index);
    onUpdateData({ ...data, socialLinks: next });
  };

  const handleIconFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const index = uploadIndexRef.current;
    compressImageFilePng(file)
      .then((iconUrl) => {
        if (index >= 0) updateSocial(index, { iconUrl });
      })
      .catch(() => {});
    e.target.value = '';
    uploadIndexRef.current = -1;
  };

  const triggerIconUpload = (index: number) => {
    uploadIndexRef.current = index;
    fileInputRef.current?.click();
  };

  const handleBrandLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    compressImageFilePng(file)
      .then((url) => onUpdateData({ ...data, brandLogoUrl: url }))
      .catch(() => {});
    e.target.value = '';
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={handleIconFileChange}
        aria-label="Upload social icon"
      />
      <input
        ref={brandLogoInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={handleBrandLogoChange}
        aria-label="Upload brand logo"
      />
      <div className={styles.editorBlock}>
        <div className={styles.editorBlockTitle}>Brand</div>
        <div className={styles.brandLogoActions}>
          <button
            type="button"
            className={styles.browseBtn}
            onClick={() => brandLogoInputRef.current?.click()}
          >
            Upload logo
          </button>
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => onUpdateData({ ...data, brandLogoUrl: '' })}
            disabled={!d.brandLogoUrl}
          >
            Clear
          </button>
        </div>
        <input
          type="text"
          className={styles.copyrightInput}
          value={(data.brandTitle as string) || ''}
          onChange={(e) =>
            onUpdateData({ ...data, brandTitle: e.target.value })
          }
          placeholder={DEFAULT_FOOTER_DATA.brandTitle}
        />
        <input
          type="text"
          className={styles.copyrightInput}
          value={(data.brandSubtitle as string) || ''}
          onChange={(e) =>
            onUpdateData({ ...data, brandSubtitle: e.target.value })
          }
          placeholder={DEFAULT_FOOTER_DATA.brandSubtitle}
        />
        <textarea
          className={styles.textAreaInput}
          value={(data.brandDescription as string) || ''}
          onChange={(e) =>
            onUpdateData({ ...data, brandDescription: e.target.value })
          }
          placeholder={DEFAULT_FOOTER_DATA.brandDescription}
          rows={3}
        />
      </div>
      <div className={styles.editorBlock}>
        <div className={styles.editorBlockTitle}>Quick Links</div>
      </div>
      <div className={styles.linksEdit}>
        {(d.links ?? []).map((link, i) => (
          <div key={i} className={styles.linkRow}>
            <input
              type="text"
              value={link.label}
              onChange={(e) => updateLink(i, 'label', e.target.value)}
              placeholder="Label"
            />
            <input
              type="text"
              value={link.url}
              onChange={(e) => updateLink(i, 'url', e.target.value)}
              placeholder="path or link (ex. /pagename)"
            />
            <button
              type="button"
              className={styles.removeBtn}
              onClick={() => removeLink(i)}
            >
              ×
            </button>
          </div>
        ))}
        <button type="button" className={styles.addBtn} onClick={addLink}>
          + Add link
        </button>
      </div>
      <div className={styles.editorBlock}>
        <div className={styles.editorBlockTitle}>Contact Us</div>
        <input
          type="text"
          className={styles.copyrightInput}
          value={(data.contactAddress as string) || ''}
          onChange={(e) =>
            onUpdateData({ ...data, contactAddress: e.target.value })
          }
          placeholder={DEFAULT_FOOTER_DATA.contactAddress}
        />
        <input
          type="text"
          className={styles.copyrightInput}
          value={(data.contactPhone as string) || ''}
          onChange={(e) =>
            onUpdateData({ ...data, contactPhone: e.target.value })
          }
          placeholder={DEFAULT_FOOTER_DATA.contactPhone}
        />
        <input
          type="text"
          className={styles.copyrightInput}
          value={(data.contactEmail as string) || ''}
          onChange={(e) =>
            onUpdateData({ ...data, contactEmail: e.target.value })
          }
          placeholder={DEFAULT_FOOTER_DATA.contactEmail}
        />
      </div>
      <div className={styles.socialEdit}>
        <div className={styles.socialEditHeader}>Social icons</div>
        {(d.socialLinks ?? []).map((s, i) => (
          <div key={i} className={styles.socialRow}>
            <div className={styles.socialIconPreview}>
              {s.iconUrl ? (
                <img src={s.iconUrl} alt="" />
              ) : (
                renderDefaultSocialIcon(i)
              )}
            </div>
            <button
              type="button"
              className={styles.browseBtn}
              onClick={() => triggerIconUpload(i)}
            >
              Upload
            </button>
            <input
              type="text"
              value={s.url ?? ''}
              onChange={(e) => updateSocial(i, { url: e.target.value })}
              placeholder="path or link (ex. /pagename)"
            />
            <label className={styles.showLabel}>
              <input
                type="checkbox"
                checked={s.visible !== false}
                onChange={(e) => updateSocial(i, { visible: e.target.checked })}
              />
              Show
            </label>
            {i >= 3 && (
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => removeSocial(i)}
                aria-label="Remove social icon"
              >
                ×
              </button>
            )}
          </div>
        ))}
        <input
          type="text"
          className={styles.copyrightInput}
          value={(data.connectTagline as string) || ''}
          onChange={(e) =>
            onUpdateData({ ...data, connectTagline: e.target.value })
          }
          placeholder={DEFAULT_FOOTER_DATA.connectTagline}
        />
      </div>
      <div className={styles.editorBlock}>
        <div className={styles.editorBlockTitle}>Copyright</div>
        <input
          type="text"
          className={styles.copyrightInput}
          value={(data.copyrightText as string) || ''}
          onChange={(e) =>
            onUpdateData({ ...data, copyrightText: e.target.value })
          }
          placeholder={DEFAULT_FOOTER_DATA.copyrightText}
        />
      </div>
    </>
  );
}

interface FooterSectionProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function FooterSection({ data }: FooterSectionProps) {
  const d = getData(data);
  const brandLogo = d.brandLogoUrl || logoPlaceholder;
  const hasContact = d.contactAddress || d.contactPhone || d.contactEmail;
  const hasSocial = (d.socialLinks ?? []).some(
    (s, i) => s.visible !== false && (Boolean(s.iconUrl) || i < 3),
  );

  return (
    <footer className={styles.section}>
      <div className={styles.grid}>
        <div className={styles.column}>
          <div className={styles.brand}>
            <div className={styles.brandLogoWrap}>
              <img src={brandLogo} alt="" className={styles.brandLogo} />
            </div>
            {(d.brandTitle || d.brandSubtitle) && (
              <div className={styles.brandTitles}>
                {d.brandTitle && (
                  <span className={styles.brandTitle}>{d.brandTitle}</span>
                )}
                {d.brandSubtitle && (
                  <span className={styles.brandSubtitle}>
                    {d.brandSubtitle}
                  </span>
                )}
              </div>
            )}
            {d.brandDescription && (
              <p className={styles.brandDescription}>{d.brandDescription}</p>
            )}
          </div>
        </div>
        <div className={styles.column}>
          <h3 className={styles.columnHeading}>Quick Links</h3>
          <ul className={styles.quickLinks}>
            {(d.links ?? []).map((link, i) => (
              <li key={i}>
                <a
                  href={(link.url ?? '').trim() || '/'}
                  className={styles.quickLink}
                  target={
                    isExternalUrl((link.url ?? '').trim())
                      ? '_blank'
                      : undefined
                  }
                  rel={
                    isExternalUrl((link.url ?? '').trim())
                      ? 'noreferrer noopener'
                      : undefined
                  }
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.column}>
          <h3 className={styles.columnHeading}>Contact Us</h3>
          {hasContact ? (
            <ul className={styles.contactList}>
              {d.contactAddress && (
                <li className={styles.contactItem}>
                  <span className={styles.contactIcon}>
                    <MapPin size={18} strokeWidth={2} />
                  </span>
                  <span>{d.contactAddress}</span>
                </li>
              )}
              {d.contactPhone && (
                <li className={styles.contactItem}>
                  <span className={styles.contactIcon}>
                    <Phone size={18} strokeWidth={2} />
                  </span>
                  <a
                    href={`tel:${d.contactPhone}`}
                    className={styles.contactLink}
                  >
                    {d.contactPhone}
                  </a>
                </li>
              )}
              {d.contactEmail && (
                <li className={styles.contactItem}>
                  <span className={styles.contactIcon}>
                    <Mail size={18} strokeWidth={2} />
                  </span>
                  <a
                    href={`mailto:${d.contactEmail}`}
                    className={styles.contactLink}
                  >
                    {d.contactEmail}
                  </a>
                </li>
              )}
            </ul>
          ) : (
            <p className={styles.contactPlaceholder}>
              Add address, phone, email in editor.
            </p>
          )}
        </div>
        <div className={styles.column}>
          <h3 className={styles.columnHeading}>Connect With Us</h3>
          {hasSocial && (
            <div className={styles.socialRowPreview}>
              {(d.socialLinks ?? []).map((s, i) =>
                s.visible !== false && (s.iconUrl || i < 3) ? (
                  <a
                    key={i}
                    href={getSocialHref(s.url, i)}
                    className={styles.socialLink}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Social link"
                  >
                    {s.iconUrl ? (
                      <img src={s.iconUrl} alt="" />
                    ) : (
                      <span className={styles.socialDefaultIcon}>
                        {renderDefaultSocialIcon(i)}
                      </span>
                    )}
                  </a>
                ) : null,
              )}
            </div>
          )}
          {d.connectTagline && (
            <p className={styles.connectTagline}>{d.connectTagline}</p>
          )}
        </div>
      </div>
      <div className={styles.bottomBar}>
        <p className={styles.copyright}>{d.copyrightText}</p>
      </div>
    </footer>
  );
}

export default FooterSection;
